import { memo, useCallback } from 'react';

import { Plus, TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';
import { cn } from '@air/utils-tailwind';

import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { DNDBoardRootDrop } from '~/components/v2/components/shared/dnd/DNDBoardRootDrop';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { WorkspaceSidebarWorkspaceBoardsMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarWorkspaceBoardsMoreDropdown';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canChangeWorkspaceDetails } from '~/utils/permissions/workspacePermissions';

const CREATE_BOARD_PLUS_BTN_TOOLTIP = 'Create new board';

export const WorkspaceSidebarWorkspaceBoardsHeader = memo(() => {
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapse = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);
  const canUpdateSubnavSort = canChangeWorkspaceDetails(permissions);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);

  const handleCreateRootBoard = useCallback(() => {
    showCreateBoardModal({
      selectedParentBoard: BOARDS_SELECT_EMPTY_OPTION,
      trackLocation: 'side-nav-extension',
    });
  }, [showCreateBoardModal]);

  return (
    <WorkspaceSidebarSectionHeader>
      <DNDBoardRootDrop asChild>
        <Button
          appearance="ghost"
          className="text-grey-9 hover:bg-grey-5 data-[dnd-over=true]:data-[dnd-droppable=true]:z-10 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-2 data-[dnd-over=true]:data-[dnd-droppable=true]:ring-blue-9 data-[active=true]:bg-grey-5"
          color="grey"
          data-testid="WORKSPACE_SIDEBAR_WORKSPACE_BOARDS_SECTION_HEADER"
          onClick={toggleCollapse}
          size="small"
          suffix={<TriangleDown className={cn('size-4 transition-transform', collapsed && '-rotate-90')} />}
        >
          Boards
        </Button>
      </DNDBoardRootDrop>
      <div className="group/workspace-sidebar-workspace-boards-header flex items-center gap-1">
        {canUpdateSubnavSort && <WorkspaceSidebarWorkspaceBoardsMoreDropdown />}
        {canCreateRootBoards && (
          <Tooltip label={CREATE_BOARD_PLUS_BTN_TOOLTIP} side="right">
            <IconButton
              icon={Plus}
              onClick={handleCreateRootBoard}
              size="small"
              color="grey"
              appearance="ghost"
              data-testid="WORKSPACE_SIDEBAR_CREATE_NEW_BOARD_BUTTON"
              label={CREATE_BOARD_PLUS_BTN_TOOLTIP}
            />
          </Tooltip>
        )}
      </div>
    </WorkspaceSidebarSectionHeader>
  );
});

WorkspaceSidebarWorkspaceBoardsHeader.displayName = 'WorkspaceSidebarWorkspaceBoardsHeader';
